import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV } from "@/data/site";
import { CopyInstall } from "@/components/site/CopyInstall";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center" aria-label="DataFrontier home">
    <img src="/brand/lockup-white.webp" alt="DataFrontier" className="h-6 w-auto md:h-7" />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-18 md:px-10">
        <Logo />

        <div className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpen(null)}>
          {NAV.map((item, i) => (
            <div key={item.label} className="relative" onMouseEnter={() => setOpen(item.children ? i : null)}>
              <Link
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="flex items-center gap-1 px-3.5 py-2 text-sm text-bone/80 transition-colors duration-300 hover:text-bone"
              >
                {item.label}
                {item.children && <ChevronDown className="h-3.5 w-3.5 opacity-50" />}
              </Link>
              <AnimatePresence>
                {open === i && item.children && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-0 top-full w-80 border border-white/10 bg-ink-850/95 p-2 backdrop-blur-xl"
                  >
                    {item.children.map((c) => (
                      <Link
                        key={c.to + c.label}
                        to={c.to}
                        data-testid={`nav-drop-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className="group block px-3 py-2.5 transition-colors duration-200 hover:bg-white/[0.04]"
                      >
                        <div className="flex items-center justify-between text-sm text-bone">
                          {c.label}
                          <ArrowUpRight className="h-3.5 w-3.5 text-mist opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        {c.desc && <div className="mt-0.5 text-xs text-mist">{c.desc}</div>}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <CopyInstall variant="nav" />
          <Link
            to="/company/contact"
            data-testid="nav-talk-to-us"
            className="hidden items-center gap-2 bg-saffron px-4 py-2 text-sm font-medium text-ink transition-colors duration-300 hover:bg-saffron-dark sm:inline-flex"
          >
            Talk to us
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((v) => !v)}
            className="text-bone lg:hidden"
            aria-label="Menu"
          >
            {mobile ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-ink lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="max-h-[80vh] space-y-1 overflow-y-auto px-5 py-6">
              {NAV.map((item) => (
                <div key={item.label} className="py-1">
                  <Link to={item.to} className="block py-2 font-serif text-2xl text-bone">
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="ml-1 border-l border-white/10 pl-4">
                      {item.children.map((c) => (
                        <Link key={c.to + c.label} to={c.to} className="block py-1.5 text-sm text-mist">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/company/contact"
                className="mt-4 inline-flex w-full items-center justify-center bg-saffron px-4 py-3 text-sm font-medium text-ink"
              >
                Talk to us
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
